import { useEffect, useRef, useState } from 'react'
import {
  AlertTriangle,
  FileSpreadsheet,
  FileText,
  Paperclip,
  RotateCcw,
  Send,
  Sparkles,
  Square,
  Wrench,
  X,
} from 'lucide-react'
import { lintBannerFromFlags, postAnswerFeedback } from '../api/queries'
import type { ConversationMessage, FeedbackReason, FeedbackVerdict } from '../api/queries'
import { cn } from '@/lib/utils/cn'
import type {
  Attachment,
  ChatMessage,
  DestructiveConfirmMessage,
  TextMessage,
} from '../hooks/useCarlaChat'
import { useCarlaChat } from '../hooks/useCarlaChat'
import type { ToolTraceItem } from '../hooks/useCarlaChat'
import { DestructiveConfirmCard } from './DestructiveConfirmCard'
import { Markdown } from '@/components/ui/Markdown'
import {
  ACCEPTED_FILE_TYPES,
  buildAttachmentPlaceholder,
  extractImagesFromClipboard,
  isImageMediaType,
  isPdfMediaType,
  renamePastedImage,
  validateAttachment,
} from './attachment-policy'
import { ChatErrorBoundary } from './ChatErrorBoundary'
import { decideHydrateAction } from './hydrate-guard'

function attachmentIcon(a: Attachment) {
  if (isPdfMediaType(a.mediaType)) {
    return <FileText size={16} />
  }
  return <FileSpreadsheet size={16} />
}

export interface CarlaChatPaneProps {
  /** Current conversation id — null means "new conversation". */
  conversationId: string | null
  /** History to seed from a conversation click; ignored when conversationId
   *  is null. Passed in from the parent page after it fetches messages. */
  seedHistory?: ConversationMessage[]
  /** Parent signals the seed is still in-flight — render a skeleton instead
   *  of the empty "Ask CARLA anything" placeholder. */
  isLoadingSeed?: boolean
  /** @deprecated 2026-05-27 — legacy Sprint 06 ConfirmationCard prop, no
   *  longer consulted now that DestructiveConfirmCard is the only path.
   *  Kept on the props interface so external callers don't break. */
  canConfirmActions?: boolean
  /** Called on the first successful send in a new conversation so the
   *  parent page can select / highlight the newly created conversation. */
  onNewConversationCreated?: (id: string) => void
  /** Fires after a message is sent so the list can refetch for ordering. */
  onMessageSent?: () => void
  /** Optional — called by the in-pane "New conversation" button. When
   *  provided, the button delegates to the parent so its `selectedId` and
   *  remount key stay in sync; without it, the button only clears
   *  hook-local state which leaves the parent dangling on the old id. */
  onNewConversationRequested?: () => void
  /**
   * Optional builder for `odoo_context` to attach to each chat send.
   * Called fresh on every send. Used by panel embeds (e.g. sales-inbox)
   * to inject the current row's state into CARLA's system prompt.
   */
  odooContext?: () => Record<string, unknown> | null | undefined
}

export function CarlaChatPane({
  conversationId,
  seedHistory,
  isLoadingSeed,
  canConfirmActions: _canConfirmActions,
  onNewConversationCreated,
  onMessageSent,
  onNewConversationRequested,
  odooContext,
}: CarlaChatPaneProps) {
  const [input, setInput] = useState('')
  // Phase 1 (2026-10): Deep dive = higher adaptive-thinking effort for this turn.
  const [deepDive, setDeepDive] = useState(false)
  // Sprint 19 hot-fix (2026-05-05): bulk attachments. Multiple files
  // (any mix of images / xlsx / PDFs) can be sent in one chat turn.
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const fileRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  // Hydration discriminators — see the hydrate useEffect below for the
  // (A)/(B)/(C) flow notes.
  const initialIdRef = useRef<string | null>(conversationId)
  const hydratedForRef = useRef<string | null>(null)

  const {
    messages,
    conversationId: liveConversationId,
    isStreaming,
    isStopping,
    activeTool,
    toolProgress,
    thinkingNote,
    sendMessage,
    executeConfirmed,
    stopStreaming,
    newConversation,
    hydrateFromHistory,
    updateMessageAt,
  } = useCarlaChat(conversationId, { getOdooContext: odooContext })

  // Hydrate from server-side seed when parent selects an existing
  // conversation. Decision logic lives in `./hydrate-guard.ts` —
  // extracted so it's unit-testable and the branch order can't drift
  // back into the Sprint Z bug (mid-stream reset wiping live state).
  //
  // See hydrate-guard.ts for the full flow documentation. This effect
  // just dispatches on the action the pure function returns.
  useEffect(() => {
    const action = decideHydrateAction({
      conversationId,
      liveConversationId,
      initialId: initialIdRef.current,
      hydratedFor: hydratedForRef.current,
      isStreaming,
      isLoadingSeed: Boolean(isLoadingSeed),
    })

    if (action === 'noop' || action === 'wait_seed') return

    if (action === 'reset') {
      newConversation()
      hydratedForRef.current = null
      return
    }

    if (action === 'mark_b_flow') {
      hydratedForRef.current = conversationId
      return
    }

    // hydrate_a — load the seed query into hook state.
    const history: ChatMessage[] = (seedHistory ?? [])
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map(
        (m): TextMessage => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
          lintBanner: lintBannerFromFlags(m.lint_flags),
          messageId: typeof m.id === 'number' ? m.id : undefined,
          toolTrace: traceFromHistory(m),
        }),
      )
    hydrateFromHistory(history, conversationId!)
    hydratedForRef.current = conversationId
  }, [
    conversationId,
    seedHistory,
    isStreaming,
    isLoadingSeed,
    liveConversationId,
    hydrateFromHistory,
    newConversation,
  ])

  // Auto-scroll on new messages / tool-use indicator
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, activeTool])

  // Surface "new" conversation id to parent once the server assigns one
  useEffect(() => {
    if (!conversationId && liveConversationId && onNewConversationCreated) {
      onNewConversationCreated(liveConversationId)
    }
  }, [conversationId, liveConversationId, onNewConversationCreated])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    let text = input.trim()
    if (!text && attachments.length === 0) return
    if (!text) {
      text = buildAttachmentPlaceholder(attachments.map((a) => a.filename))
    }
    void sendMessage(text, attachments, { deepDive }).then(() => onMessageSent?.())
    setInput('')
    setAttachments([])
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e as unknown as React.FormEvent)
    }
  }

  function ingestFiles(files: File[]) {
    if (files.length === 0) return

    // Validate all up front; surface the first failure to the user
    // (rather than partially appending good ones, which is confusing).
    const validations = files.map((f) => ({ file: f, validation: validateAttachment(f) }))
    const firstFail = validations.find((v) => !v.validation.ok)
    if (firstFail && !firstFail.validation.ok) {
      alert(firstFail.validation.message)
      return
    }

    Promise.all(
      validations.map(
        ({ file, validation }) =>
          new Promise<Attachment>((resolve, reject) => {
            if (!validation.ok) return reject(new Error('validation failed'))
            const { effectiveType } = validation
            const reader = new FileReader()
            reader.onload = () => {
              const dataUrl = reader.result as string
              const base64 = dataUrl.split(',')[1] ?? ''
              resolve({
                base64,
                mediaType: effectiveType,
                previewDataUrl: isImageMediaType(effectiveType) ? dataUrl : '',
                filename: file.name,
              })
            }
            reader.onerror = () => reject(reader.error ?? new Error('read error'))
            reader.readAsDataURL(file)
          }),
      ),
    )
      .then((newOnes) => setAttachments((prev) => [...prev, ...newOnes]))
      .catch(() => alert('Could not read one of the selected files.'))
  }

  function handleFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    ingestFiles(files)
    e.target.value = ''
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    // Only intercept when the clipboard carries image files. Plain-text
    // pastes fall through to the textarea's default behavior.
    const images = extractImagesFromClipboard(e.clipboardData?.items)
    if (images.length === 0) return
    e.preventDefault()
    const renamed = images.map((f) => renamePastedImage(f))
    ingestFiles(renamed)
  }

  function removeAttachment(idx: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== idx))
  }

  const isEmpty = messages.length === 0 && !activeTool
  const showSeedSkeleton = Boolean(isLoadingSeed) && messages.length === 0 && !isStreaming

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <header className="flex flex-shrink-0 items-center justify-end gap-2 border-b border-border-subtle bg-surface px-4 py-2">
        <button
          type="button"
          // Delegate to parent if it provided a handler — this lets the
          // parent bump its remount key + clear selectedId so its state
          // and ours stay in sync. Falls back to hook-local clear when
          // no parent handler is wired (older callers).
          onClick={() => {
            if (onNewConversationRequested) onNewConversationRequested()
            else newConversation()
          }}
          disabled={isStreaming}
          title="New conversation"
          aria-label="New conversation"
          className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RotateCcw size={14} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto bg-canvas px-4 py-3">
        <ChatErrorBoundary
          onReload={() => {
            // Bump the chatInstanceKey via parent to force a fresh
            // mount (clears all hook state, refetches seed). This
            // matches the +New / select-conversation reset pattern.
            if (onNewConversationRequested) onNewConversationRequested()
            else newConversation()
          }}
        >
          {showSeedSkeleton ? (
            <div
              role="status"
              aria-label="Loading conversation"
              className="flex flex-col gap-3"
            >
              <div className="h-14 w-2/3 animate-pulse self-start rounded-sm bg-surface-sunk" />
              <div className="h-20 w-3/4 animate-pulse self-end rounded-sm bg-surface-sunk" />
              <div className="h-10 w-1/2 animate-pulse self-start rounded-sm bg-surface-sunk" />
            </div>
          ) : isEmpty ? (
            <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
              <Sparkles size={24} className="text-tertiary opacity-40" />
              <p className="m-0 text-sm font-medium text-secondary">Ask CARLA anything</p>
              <p className="m-0 text-xs text-tertiary">
                Orders, inventory, margins, CofAs, forecasts, actions.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {messages.map((msg, i) =>
                msg.role === 'destructive_confirm' ? (
                  <DestructiveConfirmCard
                    key={`dconfirm-${i}`}
                    msg={msg as DestructiveConfirmMessage}
                    onConfirm={async (token, original) => {
                      // Deterministic execute — POST to the new
                      // /api/agent/execute-confirmed endpoint, which
                      // dispatches the EXACT (tool, params) we hashed
                      // at issuance and streams the summary turn back.
                      // No Claude re-emit, no params_hash drift. Returns
                      // whether the tool actually dispatched so the card
                      // knows whether to flip to "Confirmed" or stay
                      // retryable.
                      return await executeConfirmed(
                        original.tool_name,
                        original.tool_params,
                        token,
                      )
                    }}
                    onUpdateStatus={(status) => updateMessageAt(i, { status })}
                    busy={isStreaming}
                  />
                ) : (
                  <ChatBubble key={i} conversationId={liveConversationId} msg={msg as TextMessage} />
                ),
              )}
              {activeTool ? <ToolIndicator name={activeTool} progress={toolProgress} note={thinkingNote} /> : null}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ChatErrorBoundary>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-shrink-0 flex-col gap-1.5 border-t border-border-subtle bg-surface px-3 py-2"
      >
        {attachments.length > 0 ? (
          <div className="flex flex-col gap-1">
            {attachments.map((a, idx) => (
              <div
                key={`${a.filename}-${idx}`}
                className="flex items-center gap-2 rounded-xs border border-border-subtle bg-surface-sunk px-2 py-1 text-xs"
              >
                {isImageMediaType(a.mediaType) && a.previewDataUrl ? (
                  <img
                    src={a.previewDataUrl}
                    alt="attachment preview"
                    className="h-8 w-8 rounded-xs object-cover"
                  />
                ) : (
                  <div
                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xs bg-surface text-accent"
                    aria-hidden="true"
                  >
                    {attachmentIcon(a)}
                  </div>
                )}
                <span className="flex-1 truncate text-secondary">
                  {a.filename ?? a.mediaType}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(idx)}
                  aria-label={`Remove ${a.filename ?? 'attachment'}`}
                  className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        {isStreaming ? (
          <div>
            <button
              type="button"
              onClick={stopStreaming}
              disabled={isStopping}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-xs border border-border-default bg-surface-sunk px-2.5 py-1 text-xs font-semibold text-secondary hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Square size={12} /> {isStopping ? 'Stopping…' : 'Stop'}
            </button>
          </div>
        ) : null}
        <div className="flex items-end gap-1.5">
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={[...ACCEPTED_FILE_TYPES, '.xlsx', '.pdf', '.docx', '.csv', '.txt', '.md'].join(',')}
            className="hidden"
            onChange={handleFilePick}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={isStreaming}
            title="Attach files — images, PDF, Word (.docx), Excel (.xlsx), CSV, or text (one or more)"
            aria-label="Attach files"
            className="inline-flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-xs border border-border-default bg-surface text-tertiary hover:bg-surface-hover hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Paperclip size={14} />
          </button>
          <button
            type="button"
            onClick={() => setDeepDive((v) => !v)}
            disabled={isStreaming}
            aria-pressed={deepDive}
            aria-label="Deep dive"
            title="Deep dive — spend more reasoning on this question (diagnostics, root causes)"
            className={cn(
              'inline-flex h-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-xs border px-2 text-xs font-semibold',
              deepDive
                ? 'border-accent bg-accent text-on-accent hover:bg-accent-hover'
                : 'border-border-default bg-surface text-tertiary hover:bg-surface-hover hover:text-primary',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            Deep dive
          </button>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            rows={1}
            disabled={isStreaming}
            placeholder="Ask CARLA…  (paste images directly)"
            className={cn(
              'min-h-8 flex-1 resize-none rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary outline-none',
              'placeholder:text-tertiary focus:border-border-focus',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          />
          <button
            type="submit"
            disabled={isStreaming || (!input.trim() && attachments.length === 0)}
            aria-label="Send"
            className="inline-flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-xs border-none bg-accent text-on-accent hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send size={14} />
          </button>
        </div>
      </form>
    </div>
  )
}

/** Phase 4 (2026-10): rebuild a tool trace from a reloaded history entry. */
function traceFromHistory(m: ConversationMessage): ToolTraceItem[] | undefined {
  if (!m.tool_calls || m.tool_calls.length === 0) return undefined
  const results = new Map((m.tool_results ?? []).map((r) => [r.tool_call_id, r]))
  return m.tool_calls.map((c) => {
    const r = c.id ? results.get(c.id) : undefined
    const res = r?.result as Record<string, unknown> | undefined
    return {
      id: c.id,
      name: c.name,
      input: c.input,
      result: r?.result,
      ok: r ? !(r.is_error || (res && (res.error || res.ok === false))) : undefined,
      truncated: Boolean(res && (res.truncated || res._truncated)),
      total: typeof res?.total === 'number' ? (res.total as number) : null,
    }
  })
}

function shortJson(v: unknown, max = 140): string {
  try {
    const s = JSON.stringify(v)
    return s.length > max ? s.slice(0, max) + '…' : s
  } catch {
    return String(v)
  }
}

/** Phase 4 (2026-10): collapsible "What CARLA checked" under an answer. */
function ToolTrace({ trace }: { trace: ToolTraceItem[] }) {
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState<number | null>(null)
  const errors = trace.filter((t) => t.ok === false).length
  return (
    <div className="mt-2 text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-tertiary hover:text-primary"
      >
        <Wrench size={12} />
        {open ? 'Hide' : 'What CARLA checked'} · {trace.length} tool call{trace.length === 1 ? '' : 's'}
        {errors ? ` · ${errors} failed` : ''}
      </button>
      {open ? (
        <ol className="mt-1 flex flex-col gap-1 border-l border-border-default pl-2">
          {trace.map((t, i) => (
            <li key={`${t.id ?? i}`} className="flex flex-col gap-0.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-semibold text-secondary">{t.name}</span>
                {t.ok === false ? <span className="text-danger-text">error</span> : null}
                {t.truncated ? <span className="text-warning-text">truncated</span> : null}
                {typeof t.total === 'number' ? <span className="text-tertiary">total {t.total}</span> : null}
                <span className="truncate text-tertiary">{shortJson(t.input ?? {})}</span>
                {t.result !== undefined ? (
                  <button
                    type="button"
                    onClick={() => setExpanded((cur) => (cur === i ? null : i))}
                    className="cursor-pointer border-none bg-transparent p-0 text-accent hover:underline"
                  >
                    {expanded === i ? 'hide result' : 'result'}
                  </button>
                ) : null}
              </div>
              {expanded === i ? (
                <pre className="max-h-64 overflow-auto rounded-xs bg-surface-sunk p-2 text-[11px] leading-snug text-secondary">
                  {JSON.stringify(t.result, null, 2)?.slice(0, 20000)}
                </pre>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  )
}

/** Phase 4 (2026-10): 👍 / 👎 with a reason; a 👎 becomes an eval candidate. */
function AnswerFeedback({ msg, conversationId }: { msg: TextMessage; conversationId: string | null }) {
  const [state, setState] = useState<'idle' | 'reason' | 'sent' | 'error'>('idle')
  const [reason, setReason] = useState<FeedbackReason>('wrong_data')
  const [note, setNote] = useState('')
  if (!conversationId) return null
  const send = async (verdict: FeedbackVerdict, r?: FeedbackReason) => {
    try {
      await postAnswerFeedback({
        conversation_id: conversationId,
        message_id: msg.messageId,
        verdict,
        reason: r,
        note: note || undefined,
        answer_excerpt: msg.content.slice(0, 600),
        context: { tools: (msg.toolTrace ?? []).map((t) => t.name), lint: msg.lintBanner ?? null },
      })
      setState('sent')
    } catch {
      setState('error')
    }
  }
  if (state === 'sent') return <div className="mt-1 text-xs text-tertiary">Thanks — recorded.</div>
  if (state === 'error') return <div className="mt-1 text-xs text-danger-text">Couldn't record feedback.</div>
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-tertiary">
      <button type="button" aria-label="Good answer" onClick={() => void send('up')}
        className="cursor-pointer rounded-xs border border-border-default bg-surface px-1.5 py-0.5 hover:bg-surface-hover">👍</button>
      <button type="button" aria-label="Bad answer" onClick={() => setState('reason')}
        className="cursor-pointer rounded-xs border border-border-default bg-surface px-1.5 py-0.5 hover:bg-surface-hover">👎</button>
      {state === 'reason' ? (
        <>
          <select value={reason} onChange={(e) => setReason(e.target.value as FeedbackReason)}
            className="rounded-xs border border-border-default bg-surface px-1 py-0.5 text-xs text-primary">
            <option value="wrong_data">Wrong data</option>
            <option value="unverified">Claimed without checking</option>
            <option value="unhelpful">Unhelpful</option>
            <option value="slow">Too slow</option>
            <option value="other">Other</option>
          </select>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was wrong? (optional)"
            className="min-w-40 rounded-xs border border-border-default bg-surface px-1.5 py-0.5 text-xs text-primary placeholder:text-tertiary" />
          <button type="button" onClick={() => void send('wrong', reason)}
            className="cursor-pointer rounded-xs border-none bg-accent px-2 py-0.5 text-xs font-semibold text-on-accent hover:bg-accent-hover">Send</button>
        </>
      ) : null}
    </div>
  )
}

function ChatBubble({ msg, conversationId }: { msg: TextMessage; conversationId: string | null }) {
  const isUser = msg.role === 'user'
  return (
    <div className={cn('flex gap-2', isUser && 'flex-row-reverse')}>
      {!isUser ? (
        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-accent-subtle text-accent-text">
          <Sparkles size={12} />
        </div>
      ) : null}
      <div
        className={cn(
          'max-w-[75%] rounded-sm px-3 py-2 text-sm',
          isUser
            ? 'bg-accent text-on-accent'
            : msg.error
              ? 'bg-danger-bg text-danger-text'
              : 'bg-surface text-primary shadow-sm',
        )}
      >
        {msg.attachmentPreview ? (
          <img
            src={msg.attachmentPreview}
            alt="attachment"
            className="mb-1 max-h-40 rounded-xs"
          />
        ) : null}
        {isUser ? (
          <p className="m-0 whitespace-pre-wrap leading-snug">{msg.content}</p>
        ) : (
          <div
            role={msg.streaming ? 'status' : undefined}
            aria-live={msg.streaming ? 'polite' : undefined}
            aria-busy={msg.streaming || undefined}
          >
            {msg.content ? <Markdown>{msg.content}</Markdown> : null}
            {msg.streaming ? (
              <span
                className="ml-0.5 inline-block h-3 w-2 animate-pulse bg-tertiary align-baseline"
                aria-hidden="true"
              />
            ) : null}
            {msg.role === 'assistant' && !msg.streaming && msg.toolTrace && msg.toolTrace.length > 0 ? (
              <ToolTrace trace={msg.toolTrace} />
            ) : null}
            {msg.role === 'assistant' && !msg.streaming && msg.content ? (
              <AnswerFeedback msg={msg} conversationId={conversationId} />
            ) : null}
            {msg.lintBanner && !msg.streaming ? (
              <div
                role="note"
                className="mt-2 flex items-start gap-2 rounded-sm border border-warning-border bg-warning-bg px-2 py-1.5 text-xs text-warning-text"
              >
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{msg.lintBanner}</span>
              </div>
            ) : null}
            {msg.loopSuspected && !msg.streaming ? (
              <div
                role="alert"
                className="mt-2 flex items-start gap-2 rounded-sm border border-warning-border bg-warning-bg px-2 py-1.5 text-xs text-warning-text"
              >
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>
                  CARLA stopped before completing this turn. The last action
                  may not have run — try rephrasing or breaking the request
                  into smaller steps.
                </span>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

interface ToolProgress {
  name: string
  done: number
  total: number
  lastKey?: string | null
}

function ToolIndicator({
  name,
  progress,
  note,
}: {
  name: string
  progress?: ToolProgress | null
  /** Phase 1 (2026-10): latest adaptive-thinking summary from the model. */
  note?: string
}) {
  let label: string
  if (progress && progress.name === name) {
    const base = `Running ${name.replace(/_/g, ' ')} (${progress.done}/${progress.total})`
    label = progress.lastKey ? `${base} — last: ${progress.lastKey}` : base + '…'
  } else if (name === 'thinking') {
    label = 'Thinking…'
  } else {
    label = `Running ${name.replace(/_/g, ' ')}…`
  }
  return (
    <div role="status" aria-live="polite" className="text-role-metadata flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-1.5">
        <Wrench size={12} /> {label}
      </span>
      {note ? <span className="truncate text-xs italic text-tertiary">{note}</span> : null}
    </div>
  )
}
